import { useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";
import { AxiosError } from "axios";

export function useCreate<T, K>({
  endpoint,
  queryKeys,
  variableKey,
  name,
}: {
  endpoint: string;
  queryKeys: string[];
  variableKey?: keyof T;
  name: string;
}) {
  const getQueryKeys = (variables: T) => {
    if (variableKey && variables && variables[variableKey]) {
      return [...queryKeys, variables[variableKey]];
    }
    return queryKeys;
  };

  return function useCreateMT({ onSuccess }: { onSuccess?: (data: K) => void }) {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (payload: T) => {
        const response = await axiosInstance.post<K>(endpoint, payload, {
          baseURL: "/api/proxy",
        });
        return response.data;
      },

      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: getQueryKeys(variables),
        });
        const previousData = queryClient.getQueryData(getQueryKeys(variables));

        queryClient.setQueryData(getQueryKeys(variables), (old: { data: T[] } | undefined) => {
          if (!old?.data) return old;
          console.log("old", old);
          return {
            ...old,
            data: [
              {
                id: "temp-" + Date.now(),
                ...variables,
                updatedAt: new Date().toISOString(),
              },
              ...old.data,
            ],
          };
        });

        return { previousData };
      },

      onError: (_err, variables, context) => {
        console.log("err", _err);
        queryClient.setQueryData(getQueryKeys(variables), context?.previousData);
        const axiosError = _err as AxiosError<{ error?: string }>;
        const errorMsg =
          axiosError?.response?.data?.error || `Failed to add ${name}. Please try again.`;
        toast.error(errorMsg);
      },

      onSuccess: (data) => {
        toast.success(`${name} added successfully`);
        onSuccess?.(data);
      },

      onSettled: (_data, _err, variables) => {
        queryClient.invalidateQueries({
          queryKey: getQueryKeys(variables),
        });
      },
    });
  };
}
